import { dehydrate, DehydratedState, hydrate, Query, QueryClient, QueryKey } from '@tanstack/react-query'

const STORAGE_KEY = 'kodiak:query-cache:v2'
// Balances refetch every few seconds; one write per window keeps storage churn low.
const SAVE_THROTTLE_MS = 5_000
const MAX_AGE_MS = 24 * 60 * 60 * 1000
const BIGINT_TAG = '__bigint'

export type PersistRule = {
  /** First element of the query keys this rule covers. */
  prefix: string
  /** The most recently updated queries kept, bounding storage for keys with many variants. */
  maxEntries: number
  /** Keeps only queries of the most recently updated group, e.g. the active account's balances. */
  groupBy?: (queryKey: QueryKey) => unknown
}

// Balances are bigints, which JSON cannot carry as-is.
const replacer = (_key: string, value: unknown) =>
  typeof value === 'bigint' ? { [BIGINT_TAG]: value.toString() } : value

const reviver = (_key: string, value: unknown) =>
  value && typeof value === 'object' && BIGINT_TAG in value
    ? BigInt((value as Record<typeof BIGINT_TAG, string>)[BIGINT_TAG])
    : value

const ruleFor = (queryKey: QueryKey, rules: readonly PersistRule[]) =>
  rules.find(({ prefix }) => queryKey[0] === prefix)

function selectQueries(queries: Query[], rules: readonly PersistRule[]): Set<string> {
  const kept = new Set<string>()
  for (const rule of rules) {
    let matching = queries
      .filter(({ queryKey, state }) => queryKey[0] === rule.prefix && state.status === 'success' && state.data != null)
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    const { groupBy } = rule
    if (groupBy && matching.length > 0) {
      const latestGroup = groupBy(matching[0].queryKey)
      matching = matching.filter(({ queryKey }) => groupBy(queryKey) === latestGroup)
    }
    matching.slice(0, rule.maxEntries).forEach(({ queryHash }) => kept.add(queryHash))
  }
  return kept
}

function restore(queryClient: QueryClient, rules: readonly PersistRule[]) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return
    const state = JSON.parse(stored, reviver) as DehydratedState
    if (!Array.isArray(state?.queries)) return
    const now = Date.now()
    const queries = state.queries.filter(
      ({ queryKey, state: { dataUpdatedAt } }) =>
        Array.isArray(queryKey) && ruleFor(queryKey, rules) && now - dataUpdatedAt < MAX_AGE_MS
    )
    // Restored queries may sit unobserved until their screen opens; collecting them
    // after the default five minutes would bring the loading state back.
    hydrate(queryClient, { mutations: [], queries }, { defaultOptions: { queries: { gcTime: MAX_AGE_MS } } })
  } catch {
    localStorage.removeItem(STORAGE_KEY)
  }
}

/**
 * Keeps the data of the queries covered by `rules` in localStorage. On start the
 * stored data is restored with its original fetch time, so it renders at once
 * and refetches quietly once stale. Returns a function that stops persisting.
 */
export function persistQueries(queryClient: QueryClient, rules: readonly PersistRule[]): () => void {
  restore(queryClient, rules)

  let lastWritten: string | undefined
  let saveTimer: number | undefined
  const save = () => {
    clearTimeout(saveTimer)
    saveTimer = undefined
    const kept = selectQueries(queryClient.getQueryCache().getAll(), rules)
    const serialized = JSON.stringify(
      dehydrate(queryClient, { shouldDehydrateQuery: ({ queryHash }) => kept.has(queryHash) }),
      replacer
    )
    if (serialized === lastWritten) return
    try {
      localStorage.setItem(STORAGE_KEY, serialized)
      lastWritten = serialized
    } catch {
      // Storage full or unavailable: queries still work, only without the warm start.
    }
  }

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return
    if (!ruleFor(event.query.queryKey, rules)) return
    saveTimer ??= window.setTimeout(save, SAVE_THROTTLE_MS)
  })
  // Flushes updates still waiting for the throttle when the page goes away.
  const flush = () => saveTimer !== undefined && save()
  window.addEventListener('pagehide', flush)

  return () => {
    unsubscribe()
    clearTimeout(saveTimer)
    window.removeEventListener('pagehide', flush)
  }
}
